import { useState, useEffect } from 'react'
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  groups: string[]
  signOut: () => Promise<void>
}

interface User {
  username: string
  email?: string
}

export function useAuth(): AuthState {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [groups, setGroups] = useState<string[]>([])

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()

      const idToken = session.tokens?.idToken
      const rawGroups = idToken?.payload['cognito:groups'] as string[] || []

      // Normalize federated group names (e.g., "us-east-1_xxx_EntraID admin" -> "admin")
      const userGroups = rawGroups.map(group => {
        // Federated groups have format: "poolId_providerName groupName"
        const spaceIndex = group.lastIndexOf(' ')
        if (spaceIndex !== -1) {
          return group.substring(spaceIndex + 1)
        }
        return group
      })

      setUser({
        username: currentUser.username,
        email: idToken?.payload.email as string,
      })
      setGroups(userGroups)
      setIsAuthenticated(true)
    } catch {
      setUser(null)
      setGroups([])
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSignOut() {
    await signOut()
    setUser(null)
    setGroups([])
    setIsAuthenticated(false)
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    groups,
    signOut: handleSignOut,
  }
}
